import { IonIcon, IonPopover } from '@ionic/react';
import { ellipsisHorizontal } from 'ionicons/icons';
import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventNotification } from '../../api/graphql/generated';
import { useIsMounted } from '../../util/hooks';
import { logger } from '../../util/logger';
import { BaseButton } from '../button/base-button';
import { IonItem } from '../ionic/IonItem';
import { PopoverState } from '../ui/ui-interfaces';

interface NotificationOptionsProps {
  notification: EventNotification;
  onMarkRead?: () => void;
}

interface NotificationOption {
  label: string;
  action: NotificationOptionAction;
  disabled?: boolean;
  display?: boolean;
}

type NotificationOptionAction = (
  e: React.MouseEvent<HTMLIonItemElement, MouseEvent>
) => void;

const NotificationOptions = (
  props: NotificationOptionsProps
): ReactElement | null => {
  const { notification, onMarkRead } = props;
  const { i18n } = useTranslation();

  const [popoverState, setShowPopover] = useState({
    showPopover: false,
    event: undefined,
  } as PopoverState);

  const isMounted = useIsMounted();

  const options: NotificationOption[] = [
    {
      label: i18n.t('common:notifications_mark_read'),
      action: () => {
        if (onMarkRead) {
          onMarkRead();
        }
      },
      display: !notification.read,
    },
    {
      label: i18n.t('common:notifications_mark_unread'),
      action: () => {
        logger.warn('Mark Unread not implemented');
      },
      display: notification.read,
    },
  ];

  const displayPopover = (
    e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    e.persist();
    if (isMounted()) {
      setShowPopover({ showPopover: true, event: e as unknown as Event });
    }
  };

  const hidePopover = () => {
    if (isMounted()) {
      setShowPopover({ showPopover: false, event: undefined });
    }
  };

  const optionActionWrapper = (action: NotificationOptionAction) => {
    return (e: React.MouseEvent<HTMLIonItemElement, MouseEvent>): void => {
      e.preventDefault();
      e.stopPropagation();
      action(e);
      hidePopover();
    };
  };

  const optionButtons = options.reduce((items, option, idx) => {
    if (!option.display) {
      return items;
    }

    items.push(
      <IonItem
        button={true}
        key={idx}
        onClick={optionActionWrapper(option.action)}
        disabled={option.disabled}
      >
        {option.label}
      </IonItem>
    );

    return items;
  }, [] as ReactElement[]);

  return optionButtons.length === 0 ? null : (
    <>
      <BaseButton
        buttonProps={{
          onClick: displayPopover,
          fill: 'clear',
          color: 'medium',
        }}
      >
        <IonIcon icon={ellipsisHorizontal}></IonIcon>
      </BaseButton>
      <IonPopover
        event={popoverState.event}
        isOpen={popoverState.showPopover}
        onDidDismiss={hidePopover}
      >
        {optionButtons}
      </IonPopover>
    </>
  );
};

export { NotificationOptions };
